import { QuestionGroupGuideline } from '@doltech/ui/lib/figma/QuestionGroupGuideline/QuestionGroupGuideline';
import { QuestionTypeEnum } from '@doltech/utils/lib/constants';
import * as mobx from 'mobx';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { ERROR_NOT_SUPPORTED } from '../../question-data-model/constants';
import { DiagramLabelCompletionAnswerSubject } from './questions/DiagramLabelCompletionAnswerSubject';
import { DragInAnswerSubject } from './questions/DragInAnswerSubject';
import { DragOutAnswerSubject } from './questions/DragOutAnswerSubject';
import { FlowChartComplexAnswerSubject } from './questions/FlowChartComplexAnswerSubject';
import { MultipleAnswerSubject } from './questions/MultipleAnswerSubject';
import { SentenceCompleteSubject } from './questions/SentenceCompletionSubject';
import { ShortAnswerSubject } from './questions/ShortAnswerSubject';
import { SingleAnswerSubject } from './questions/SingleAnswerSubject';
import { TFNAnswerSubject } from './questions/TFNAnswerSubject';
import { ViewTestCompletionNoHint } from './questions/ViewTestCompetionNoHint';
import { ViewTestCompletionWithHint } from './questions/ViewTestCompletionWithHint';
import { ViewTestFlowChartCompletion } from './questions/ViewTestFlowChartCompletion';
import { ViewTestMatchingEnding } from './questions/ViewTestMatchingEnding';
import { ViewTestMatchingFeatureName } from './questions/ViewTestMatchingFeatureName';
import { ViewTestMatchingHeading } from './questions/ViewTestMatchingHeading';
import { ViewTestMatchingParagraph } from './questions/ViewTestMatchingParagraph';
import { ViewTestTableCompletion } from './questions/ViewTestTableCompletion';
import { YNNAnswerSubject } from './questions/YNNAnswerSubject';

export interface QuestionProps {
  item: any;
  currentSection: any;
  isPdfContent?: boolean;
}

export const QuestionSubject = observer((props: QuestionProps) => {
  const { item, currentSection, isPdfContent } = props;
  const { uuid, type, no } = item;
  const defaultProps = {
    id: `question-no-${no}`,
    className: 'view-test-question-content',
    key: uuid,
    currentSection,
    isPdfContent,
  };

  switch (type) {
    case QuestionTypeEnum.SINGLE_ANSWER:
      return <SingleAnswerSubject {...defaultProps} data={item} />;

    case QuestionTypeEnum.MULTIPLE_ANSWER: {
      return <MultipleAnswerSubject {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.TFN_ANSWER: {
      return <TFNAnswerSubject {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.YNN_ANSWER: {
      return <YNNAnswerSubject {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.MATCHING_PARAGRAPH: {
      return <ViewTestMatchingParagraph {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.MATCHING_HEADING: {
      return <ViewTestMatchingHeading {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.MATCHING_ENDING: {
      return <ViewTestMatchingEnding {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.MATCHING_FEATURE_NAME: {
      return <ViewTestMatchingFeatureName {...defaultProps} data={item} />;
    }
    case QuestionTypeEnum.SENTENCE_COMPLETE:
      return <SentenceCompleteSubject {...defaultProps} data={item} />;
    case QuestionTypeEnum.SHORT_ANSWER:
      return <ShortAnswerSubject {...defaultProps} data={item} />;
    case QuestionTypeEnum.NOTE_COMPLETION_NO_HINT:
      return <ViewTestCompletionNoHint {...defaultProps} data={item} />;
    case QuestionTypeEnum.FLOWCHART_ANSWER:
      return <ViewTestFlowChartCompletion {...defaultProps} data={item} />;
    case QuestionTypeEnum.NOTE_COMPLETION_WITH_HINT:
      return <ViewTestCompletionWithHint {...defaultProps} data={item} />;
    case QuestionTypeEnum.TABLE_COMPLETION:
      return <ViewTestTableCompletion {...defaultProps} data={item} />;
    case QuestionTypeEnum.DIAGRAM_LABEL_COMPLETION:
      return <DiagramLabelCompletionAnswerSubject {...defaultProps} data={item} />;
    case QuestionTypeEnum.DRAG_IN:
      return <DragInAnswerSubject {...defaultProps} data={item} />;
    case QuestionTypeEnum.DRAG_OUT:
      return <DragOutAnswerSubject {...defaultProps} data={item} />;
    case QuestionTypeEnum.FLOW_CHART_COMPLEX:
      return <FlowChartComplexAnswerSubject {...defaultProps} data={item} />;
    default:
      // default return to know if api return the question type that was not supported
      return (
        <div>
          {type} {ERROR_NOT_SUPPORTED}
        </div>
      );
  }
});

const Main = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  .header-container {
    margin-bottom: 24px;
  }

  .view-test-question-content {
    padding: 0;
    background: transparent;
    box-shadow: none;

    &:not(:last-child) {
      margin-bottom: 24px;
    }

    .table-editor-wrap {
      pointer-events: none;
      color: transparent;
    }
  }
`;

interface GroupQuestionsProps {
  group: any;
  currentSection: any;
  isPdfContent?: boolean;
}

export const GroupQuestionsSubject = React.memo(
  ({ group, currentSection, isPdfContent }: GroupQuestionsProps) => {
    const questionGroupHeader = group.getQuestionGroupsFromNoToNo();

    return (
      <Main>
        <div className="header-container">
          <QuestionGroupGuideline
            content={mobx.toJS(group.guidelineContent)}
            title={questionGroupHeader}
            isExpanded
          />
        </div>
        {group?.questions.map((item, index) => {
          return (
            <QuestionSubject
              key={item?.uuid || index}
              item={item}
              currentSection={currentSection}
              isPdfContent={isPdfContent}
            />
          );
        })}
      </Main>
    );
  }
);
